import * as React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'

import mammoth from 'mammoth'
import { inject, observer } from 'mobx-react'

import { IUploadLog } from '~/client/graph'
import { getAttachmentFileType } from '~/client/src/shared/components/WorkflowCard/fileAttachmentHelpers'
import FileType from '~/client/src/shared/enums/FileType'
import Workflow from '~/client/src/shared/models/Workflow'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import WorkflowCategoriesStore from '~/client/src/shared/stores/domain/WorkflowCategories.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'

import AttachmentsStore from '../../Attachments.store'
import AttachmentModalContent from './components/AttachmentModalContent'
import AttachmentModalFileDetails from './components/AttachmentModalFileDetails'
import AttachmentModalHeader from './components/AttachmentModalHeader'
import { renderExcelPreview } from './renderExcelPreview'

import './AttachmentModal.scss'

interface IProps {
  store: AttachmentsStore
  attachment: IUploadLog
  fileName: string
  fileType: string
  onClose: () => void
  projectDateStore?: ProjectDateStore
  projectMembersStore?: ProjectMembersStore
  companiesStore?: CompaniesStore
  locationAttributesStore?: LocationAttributesStore
  workflowTypesStore?: WorkflowTypesStore
  userProjectsStore?: UserProjectsStore
  workflowCategoriesStore?: WorkflowCategoriesStore
  workflows?: Workflow[]
}

const failedToLoadWordDocument = 'Failed to load Word document'
const failedToLoadExcelDocument = 'Failed to load Excel document'

// TODO: re-enable once the Excel preview bug is fixed
const ENABLE_EXCEL_PREVIEW = false

interface IExcelStatus {
  wordHtml: string
  isLoading: boolean
  error: string
}

function AttachmentModal(props: IProps) {
  const {
    store,
    attachment,
    fileName,
    fileType,
    onClose,
    projectDateStore,
    projectMembersStore,
    companiesStore,
    locationAttributesStore,
    workflowTypesStore,
    userProjectsStore,
    workflowCategoriesStore,
    workflows,
  } = props
  const [excelStatus, setExcelStatus] = useState<IExcelStatus>({
    wordHtml: null,
    isLoading: false,
    error: null,
  })
  const excelContainerRef = useRef<HTMLDivElement>(null)

  const loadWordDocument = useCallback(
    async (fileUrl: string, signal: AbortSignal): Promise<void> => {
      try {
        setExcelStatus(prev => ({ ...prev, isLoading: true, error: null }))

        const response = await fetch(fileUrl, { signal })
        const arrayBuffer = await response.arrayBuffer()

        if (signal.aborted) return

        const result = await mammoth.convertToHtml({ arrayBuffer })

        if (signal.aborted) return

        setExcelStatus(prev => ({
          ...prev,
          wordHtml: result.value,
          isLoading: false,
        }))
      } catch (error) {
        if (signal.aborted) return
        console.error(failedToLoadWordDocument, error)
        setExcelStatus(prev => ({
          ...prev,
          error: failedToLoadWordDocument,
          isLoading: false,
        }))
      }
    },
    [],
  )

  const loadExcelDocument = useCallback(
    async (fileUrl: string, signal: AbortSignal): Promise<void> => {
      try {
        setExcelStatus(prev => ({ ...prev, isLoading: true, error: null }))

        const response = await fetch(fileUrl, { signal })
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const arrayBuffer = await response.arrayBuffer()

        if (signal.aborted) return

        if (!excelContainerRef.current) {
          setExcelStatus(prev => ({
            ...prev,
            error: failedToLoadExcelDocument,
            isLoading: false,
          }))
          return
        }

        await renderExcelPreview(arrayBuffer, excelContainerRef.current, signal)

        if (signal.aborted) return

        setExcelStatus(prev => ({ ...prev, isLoading: false }))
      } catch (error) {
        if (signal.aborted) return
        console.error(failedToLoadExcelDocument, error)
        setExcelStatus(prev => ({
          ...prev,
          error: failedToLoadExcelDocument,
          isLoading: false,
        }))
      }
    },
    [],
  )

  useEffect(() => {
    const abortController = new AbortController()
    const fileTypeEnum = getAttachmentFileType(attachment.fileType, '/')

    setExcelStatus({
      wordHtml: null,
      isLoading: false,
      error: null,
    })

    if (fileTypeEnum === FileType.Word) {
      loadWordDocument(attachment.fileUrl, abortController.signal)
    } else if (ENABLE_EXCEL_PREVIEW && fileTypeEnum === FileType.Excel) {
      loadExcelDocument(attachment.fileUrl, abortController.signal)
    }

    return () => {
      abortController.abort()
    }
  }, [
    attachment.fileUrl,
    attachment.fileType,
    loadWordDocument,
    loadExcelDocument,
  ])

  const handleDownload = (): void => {
    store.downloadDocumentByLink(attachment).catch(error => {
      console.error('Download failed:', error)
    })
  }

  const handlePrint = (): void => {
    window.open(attachment.fileUrl, '_blank')
  }

  return (
    <div className="attachment-modal absolute full-height bg-white z-index-highest ba-light-input-border">
      <div className="attachment-modal-content row full-height">
        <div className="attachment-modal-preview full-height   col flex-grow br-light-input-border">
          <AttachmentModalHeader
            fileName={fileName}
            onClose={onClose}
            onDownload={handleDownload}
            onPrint={handlePrint}
          />
          <AttachmentModalContent
            attachment={attachment}
            fileName={fileName}
            excelStatus={excelStatus}
            excelContainerRef={excelContainerRef}
            onDownload={handleDownload}
          />
        </div>

        <div className="attachment-modal-details col no-grow">
          <AttachmentModalFileDetails
            attachment={attachment}
            fileName={fileName}
            fileType={fileType}
            store={store}
            projectDateStore={projectDateStore}
            projectMembersStore={projectMembersStore}
            companiesStore={companiesStore}
            locationAttributesStore={locationAttributesStore}
            workflowTypesStore={workflowTypesStore}
            userProjectsStore={userProjectsStore}
            workflowCategoriesStore={workflowCategoriesStore}
            workflows={workflows}
          />
        </div>
      </div>
    </div>
  )
}

export default inject(
  'projectDateStore',
  'projectMembersStore',
  'companiesStore',
  'locationAttributesStore',
  'workflowTypesStore',
  'userProjectsStore',
  'workflowCategoriesStore',
)(observer(AttachmentModal))
